import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import _ from "lodash";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { EmptyState, SearchField, StatusChip, UserAvatar } from "../../components/mui";
import { fmtDateTime, toPlainText } from "../../design/domain";
import ListPager from "../../components/design/ListPager";
import { InlineLoading, SkeletonRows } from "../../components/loading";

/**
 * 稽核紀錄（DESIGN L616-L624，邏輯 L1034 / L1297）
 *
 * 資料量已累積到數千筆（實測 7,287 筆），原本的無限捲動會一路把整個資料表疊進 DOM，
 * 因此改成「伺服器端分頁」：換頁時用新的 skip 重抓一頁，前端任何時刻只持有當頁 20 筆。
 *
 * 搜尋與操作者篩選同樣走後端（/Audit/List 的 keyword / account 參數），不是只篩當頁——
 * 只篩當頁的話，使用者在第 1 頁搜不到的東西其實在第 12 頁，總筆數也不會反映篩選結果，
 * 會讓人以為「系統裡沒有這筆紀錄」。後端回的 total_count 已經是套完條件後的筆數，
 * 所以「共 N 筆」「第 X / Y 頁」在有無篩選時都是誠實的。
 */

/** 每頁顯示筆數 */
const PAGE_SIZE = 20;
/** 實際跟後端要的筆數：多要一筆，後端萬一不回 total_count 時還能判斷有沒有下一頁（上限 200） */
const FETCH_SIZE = PAGE_SIZE + 1;
/** 輸入後多久才真的送出搜尋（每個字都打一次 API 太吵） */
const SEARCH_DEBOUNCE = 300;
/** 後端 keyword 最多取 5 個字詞，前端先切齊，避免送了卻默默被丟掉 */
const MAX_TERMS = 5;

/**
 * 「動作」欄顯示的中文是後端由 action / field_key 翻出來的（DB 存的是 status_change、
 * field_key=status 這類英文碼），中文直接丟給後端一定 0 筆。
 * 只對「整個字詞剛好等於某個動作名稱」的關鍵字換成代碼，其餘原樣送出——
 * 像「建立」「留言」在描述文字裡本來就搜得到，換成代碼反而會漏掉標題含該詞的列。
 * （「欄位變更」是前端對其餘 field_key 的統稱，沒有對應代碼，屬已知搜不到的字詞。）
 */
const ACT_TERM_ALIAS = {
  狀態變更: "status",
  指派變更: "assign",
  審核決議: "review_decision",
  改派把關者: "review_reassign",
  進度更新: "progress",
  狀態流轉: "transition",
  工時登錄: "worklog",
};

/** 依 key 順序取第一個有值的欄位——後端可能回 snake_case 或 PascalCase */
const pick = (o, ...keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const v = _.get(o, keys[i]);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

/**
 * 後端目前回 { data_list, total_count, skip, take }；
 * 但歷史上也出現過裸陣列與 list / items / rows 包裝，全部吸收成陣列，
 * 拿到 {}、null、data_list 是物件等任何非陣列都降級成 []，後續 .map 不會炸。
 */
const toArray = (res) => {
  if (Array.isArray(res)) return res;
  const inner =
    _.get(res, "data_list") || _.get(res, "list") || _.get(res, "items") || _.get(res, "rows") || _.get(res, "data");
  return Array.isArray(inner) ? inner : [];
};

/** 篩選後的總筆數；後端沒給（或給了非數字）就回 null，由呼叫端改用前瞻法 */
const totalOf = (res) => {
  const n = Number(pick(res, "total_count", "totalCount", "total"));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** 後端欄位 → 設計稿 audit 形狀（L1034） */
const normalizeAudit = (a, idx) => {
  const who = String(pick(a, "actor_name", "actorName", "who_name", "whoName", "user_name", "who", "account") || "—");
  const rawWhen = pick(a, "when", "create_at", "createAt", "created_at", "createdAt", "timestamp");
  const when = rawWhen ? fmtDateTime(rawWhen) : "—";
  const target = String(pick(a, "target", "target_name", "targetName", "entity", "object") || "—");
  return {
    // 後端有給穩定的 id（fc-xxx / al-xxx）；沒有時退回「時間+對象+序號」組合當 key
    key: String(pick(a, "id", "Id", "audit_id", "auditId") || `${when}-${target}-${idx}`),
    who,
    act: String(pick(a, "action_label", "actionLabel", "action", "act") || "—"),
    target,
    /*
     * 變更前／後轉純文字：稽核攔截器記的是欄位原值，而需求說明、會議紀錄、文件備註
     * 這些欄位存的是編輯器產生的 HTML —— 原樣放進表格儲存格與 CSV 會整段吐出標籤。
     * 這裡是摘要位置（一列一格），所以轉純文字而不是渲染 HTML；toPlainText 會把
     * 只有截圖的內容標成「[圖片×N]」，不會變成看起來壞掉的空白。
     */
    before: toPlainText(pick(a, "before", "old_value", "oldValue", "before_value", "beforeValue")) || "—",
    after: toPlainText(pick(a, "after", "new_value", "newValue", "after_value", "afterValue")) || "—",
    when,
  };
};

/** 使用者輸入 → 後端 keyword：空白分隔的多個字詞（後端逐詞 AND），動作中文換成代碼 */
const buildKeyword = (raw) =>
  String(raw || "")
    .split(/[\s　]+/)
    .filter(Boolean)
    .slice(0, MAX_TERMS)
    .map((t) => ACT_TERM_ALIAS[t] || t)
    .join(" ");

function App(props) {
  const { GET_AuditList, GET_AuditFilters } = props;

  /**
   * 頁碼與篩選條件放在同一個 state：改條件時一定連頁碼一起重設回第 1 頁，
   * 分開放的話很容易出現「在第 12 頁搜尋 → 條件變了頁碼沒變 → 命中的前 220 筆被跳過」，
   * 也保證一次條件變動只觸發一次請求。
   */
  const [query, setQuery] = useState({ page: 1, keyword: "", account: "" });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  /** 搜尋框當下的文字（未送出）；送出的是 debounce 後的 query.keyword */
  const [text, setText] = useState("");
  /** 操作者下拉選項：由 /Audit/Filters 給全庫清單，不是只有當頁出現過的人 */
  const [accounts, setAccounts] = useState([]);

  // takeLatest 會取消前一個請求，用遞增序號讓過期的 callback 直接跳過，不污染新一頁的狀態
  const reqIdRef = useRef(0);

  /** 改篩選條件：一律回到第 1 頁 */
  const setFilter = useCallback((patch) => setQuery((prev) => ({ ...prev, page: 1, ...patch })), []);
  const goPage = useCallback((p) => setQuery((prev) => ({ ...prev, page: Math.max(1, p) })), []);

  const fetchPage = useCallback(
    (qy) => {
      const id = reqIdRef.current + 1;
      reqIdRef.current = id;
      const stale = () => reqIdRef.current !== id;
      let settled = false;

      setLoading(true);
      setFailed(false);

      GET_AuditList(
        {
          skip: (qy.page - 1) * PAGE_SIZE,
          take: FETCH_SIZE,
          keyword: qy.keyword || undefined,
          account: qy.account || undefined,
        },
        (data) => {
          if (stale()) return;
          settled = true;
          const list = toArray(data);
          const t = totalOf(data);
          setRows(list.slice(0, PAGE_SIZE).map(normalizeAudit));
          setTotal(t);
          setHasNext(list.length > PAGE_SIZE);
          setLoading(false);
          // 資料在翻頁期間被刪到剩不到這麼多頁時，夾回最後一頁，不要停在空白頁
          if (list.length === 0 && qy.page > 1) {
            const last = t && t > 0 ? Math.ceil(t / PAGE_SIZE) : 1;
            if (last < qy.page) setQuery((prev) => (prev.page === qy.page ? { ...prev, page: last } : prev));
          }
        },
        // saga 失敗時只會走 finally 的 loading(false)、不會回 callback，用它來判定失敗
        (busy) => {
          if (busy || settled || stale()) return;
          setRows([]);
          setTotal(null);
          setHasNext(false);
          setFailed(true);
          setLoading(false);
        }
      );
    },
    [GET_AuditList]
  );

  useEffect(() => {
    fetchPage(query);
  }, [query, fetchPage]);

  /* 操作者下拉：全庫選項只需要取一次 */
  useEffect(() => {
    let alive = true;
    GET_AuditFilters({}, (data) => {
      if (!alive) return;
      const arr = _.get(data, "accounts");
      setAccounts(Array.isArray(arr) ? arr.filter((a) => a && a.value) : []);
    });
    return () => {
      alive = false;
    };
  }, [GET_AuditFilters]);

  /* 打字停 300ms 才送出；關鍵字沒真的變（例如打了字又刪回原狀）就不重抓、也不跳頁 */
  useEffect(() => {
    const next = buildKeyword(text);
    const t = next === query.keyword ? null : setTimeout(() => setFilter({ keyword: next }), SEARCH_DEBOUNCE);
    return () => {
      if (t) clearTimeout(t);
    };
  }, [text, query.keyword, setFilter]);

  const filtering = Boolean(query.keyword || query.account);
  /** 後端沒回 total_count 時的退路：用「多要的那一筆」推算還有沒有下一頁 */
  const totalPages = total != null ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : query.page + (hasNext ? 1 : 0);
  const totalLabel = total != null ? total : (query.page - 1) * PAGE_SIZE + rows.length;

  const countChip = useMemo(() => {
    if (loading) return <InlineLoading text="載入中…" />;
    if (failed) return "—";
    return filtering ? `符合 ${totalLabel.toLocaleString()} 筆` : `共 ${totalLabel.toLocaleString()} 筆`;
  }, [loading, failed, filtering, totalLabel]);

  /** 匯出目前這一頁的稽核列為 CSV（加 BOM 讓 Excel 正確辨識 UTF-8） */
  const exportReport = () => {
    const head = ["操作者", "動作", "對象", "變更前", "變更後", "時間"];
    const body = rows.map((a) => [a.who, a.act, a.target, a.before, a.after, a.when]);
    const csv = [head, ...body]
      .map((r) => r.map((c) => `"${String(c == null ? "" : c).replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    const blob = new Blob([String.fromCharCode(0xfeff) + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-p${query.page}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /** 表格內容：載入中／失敗／空／有資料四種狀態 */
  const emptyRow = (title) => (
    <TableRow>
      <TableCell colSpan={5}>
        <EmptyState dense text={title} />
      </TableCell>
    </TableRow>
  );

  const body = () => {
    if (loading) {
      // 一頁 20 筆，骨架畫 6 列就夠撐出表格的樣子
      return (
        <TableRow>
          <TableCell colSpan={5} sx={{ p: 0 }}>
            <SkeletonRows rows={6} label="稽核紀錄載入中" />
          </TableCell>
        </TableRow>
      );
    }
    if (failed) return emptyRow("稽核紀錄載入失敗，請稍後再試");
    if (rows.length === 0) {
      return emptyRow(filtering ? "查無符合條件的紀錄，請調整關鍵字或清除篩選" : "尚無稽核紀錄");
    }
    return rows.map((a) => (
      <TableRow key={a.key}>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <UserAvatar name={a.who} />
            <Typography component="span" sx={{ fontWeight: 600 }}>
              {a.who}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <StatusChip kind="type" size="small">
            {a.act}
          </StatusChip>
        </TableCell>
        <TableCell sx={{ color: "text.secondary" }}>{a.target}</TableCell>
        <TableCell sx={{ color: "text.secondary" }}>
          <Box component="span" sx={{ textDecoration: "line-through" }}>
            {a.before}
          </Box>{" "}
          →{" "}
          <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
            {a.after}
          </Box>
        </TableCell>
        <TableCell sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {a.when}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Card className="pg">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          flexWrap: "wrap",
          gap: 1.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
          <Typography variant="h3" component="div">
            稽核紀錄{" "}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              全系統操作留痕 · 變更前後值
            </Typography>
          </Typography>
          {/* 筆數是後端套完搜尋／篩選後的總數，不是當頁筆數 */}
          <Chip size="small" label={countChip} sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }} />
        </Box>
        <Button
          variant="outlined"
          onClick={exportReport}
          disabled={rows.length === 0}
          title="匯出本頁的稽核紀錄（20 筆）"
        >
          匯出本頁
        </Button>
      </Box>

      {/* 篩選列：搜尋與操作者都送到後端，作用範圍是全部紀錄 */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
          px: 2.5,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <SearchField
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="搜尋操作者、動作、對象、變更值…"
          ariaLabel="搜尋稽核紀錄"
          title="以關鍵字搜尋全部稽核紀錄的操作者、動作、對象與變更前後值；多個關鍵字以空白分隔，需全部命中（時間欄位不參與比對）"
          sx={{ flex: 1, minWidth: 190, maxWidth: 320 }}
        />
        {/* 明講搜尋範圍是全部紀錄（不是只有當頁），使用者才知道「查無資料」是真的沒有 */}
        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
          （搜尋全部紀錄）
        </Typography>
        <TextField
          select
          value={query.account}
          onChange={(e) => setFilter({ account: e.target.value })}
          slotProps={{ select: { native: true }, htmlInput: { "aria-label": "操作者" } }}
        >
          <option value="">全部操作者</option>
          {accounts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.name || o.value}
            </option>
          ))}
        </TextField>
        {filtering && (
          <Button
            variant="text"
            onClick={() => {
              setText("");
              setQuery({ page: 1, keyword: "", account: "" });
            }}
            sx={{ whiteSpace: "nowrap" }}
          >
            清除篩選
          </Button>
        )}
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 760 }}>
          <TableHead sx={{ bgcolor: "background.default" }}>
            <TableRow>
              <TableCell>操作者</TableCell>
              <TableCell>動作</TableCell>
              <TableCell>對象</TableCell>
              <TableCell>變更</TableCell>
              <TableCell>時間</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{body()}</TableBody>
        </Table>
      </TableContainer>

      {/* 伺服器端分頁器：沿用清單共用的 ListPager（只有一頁時它自己不顯示） */}
      <ListPager
        page={query.page}
        totalPages={totalPages}
        total={totalLabel}
        onPrev={() => goPage(query.page - 1)}
        onNext={() => goPage(query.page + 1)}
      />
    </Card>
  );
}

/**
 * 本頁自行掌控 skip/take/keyword（reducer 的 Audit.List 只留最後一頁），
 * 因此不從 store 取值，mapStateToProps 傳 null 以免無謂的重繪。
 */
const mapDispatchToProps = (dispatch) => ({
  GET_AuditList: (payload, callback, loading) => dispatch({ type: "GET_AuditList", payload, callback, loading }),
  GET_AuditFilters: (payload, callback) => dispatch({ type: "GET_AuditFilters", payload, callback }),
});

App.propTypes = {
  GET_AuditList: PropTypes.func,
  GET_AuditFilters: PropTypes.func,
};

App.defaultProps = {
  GET_AuditList: () => {},
  GET_AuditFilters: () => {},
};

export default connect(null, mapDispatchToProps)(App);
